// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Screen Wake Lock — keeps a monitoring device's screen awake while armed, and
 * re-acquires it after the OS drops the lock (e.g. on tab focus). Entirely local;
 * a silent no-op where the API is missing.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (t: string, cb: () => void) => void;
}
interface WakeLockNavigator {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
}

let sentinel: WakeLockSentinelLike | null = null;
let wanted = false;

export function wakeLockSupported(): boolean {
  return 'wakeLock' in navigator;
}

export async function acquireWakeLock(): Promise<boolean> {
  wanted = true;
  const wl = (navigator as unknown as WakeLockNavigator).wakeLock;
  if (!wl) return false;
  try {
    sentinel = await wl.request('screen');
    sentinel.addEventListener('release', () => {
      sentinel = null;
    });
    return true;
  } catch {
    return false;
  }
}

export async function releaseWakeLock(): Promise<void> {
  wanted = false;
  if (sentinel && !sentinel.released) {
    try {
      await sentinel.release();
    } catch {
      /* ignore */
    }
  }
  sentinel = null;
}

/** Call on visibilitychange: re-acquire if we still want the lock and became visible. */
export async function reacquireIfWanted(): Promise<void> {
  if (wanted && !sentinel && document.visibilityState === 'visible') {
    await acquireWakeLock();
  }
}
